import { useState, useEffect, useContext, useMemo } from "react";

import { Default } from "../../../components/Default/Default";
import { PageHeading } from '../../../components/StyledComponents/styleComponents.index'
import { Copilot, LoadingContent } from '../../../components/SharedComponents/sharedComponents.index'
import { LOADING_CONTENT_TYPE } from "../../../components/SharedComponents/LoadingContent/loading.content.constants";
import { OPPORTUNITY_STATUS } from "../../../components/components.constants";
import { SYSTEM_COLORS } from "../../../components/system.colors";

import { OpportunityCounter } from '../Opportunities/Opportunity'

import { TenantContext } from '../../../templates/context/TenantContext'
import { useOtbViewModel } from '../../../viewModel/useOtbVewModel'
import { useAnalysisViewModel } from '../../../viewModel/useAnalysysViewModel'

import { i18n } from '../../../translate/i18n.js'

import { ProductHierarchyCard } from "./components/ProductHierarchyCard";
import { TrendChangesPanel } from "./components/TrendChangesPanel";
import { RecommendedActionsTable } from "./components/RecommendedActionsTable";

const { t } = i18n;

export function OpportunityInsightsScreen() {

  const { userInfo, hierarchy } = useContext(TenantContext)
  const { userId, tenant } = userInfo || {};
  const { tenantId } = tenant || {};  

  const [hierarchyPath, setHierarchyPath] = useState([]);

  const [productKeys, setProductKeys] = useState([]);

  const [focusProduct, setFocusProduct] = useState(null);
  const handleFocusProduct = (productId) => setFocusProduct({ productId, ts: Date.now() });

  const vm = useOtbViewModel({
    shouldFetchActiveOpp: OPPORTUNITY_STATUS.ACTIVE,
    shouldFetchCheckoutOpp: OPPORTUNITY_STATUS.CHECKOUT,
    shouldFetchRemovedOpp: OPPORTUNITY_STATUS.REMOVED,
    shouldFetchOpportunitiesDetails: true
  });

  const vmAnalysis = useAnalysisViewModel({ 
    shouldFetchProducts: true,
    shouldFetchForecastData: true
  });  

  const counterCtx = useMemo(() => ({
    opportunityData: vm.ctx.opportunityCounterData,
    checkoutOpportunities: vm.ctx.checkoutOpportunities?.checkoutOpportunities ?? [],
    oppTypes: vm.ctx.activeOpportunities?.opportunityTypes
  }), [
    vm.ctx.opportunityCounterData,
    vm.ctx.checkoutOpportunities?.checkoutOpportunities,
    vm.ctx.activeOpportunities?.opportunityTypes
  ]);

  useEffect(() => {
    setProductKeys(hierarchyPath.length ? [hierarchyPath[hierarchyPath.length - 1]] : []);
  }, [hierarchyPath]);

  const handleClearFilters = () => setHierarchyPath([]);

  if(vm?.uiState?.isLoadingOpportunitiesTrends || vm?.uiState?.isLoadingOpportunitiesDetails)
    return <LoadingContent label={"Recuperando oportunidades..."} type={LOADING_CONTENT_TYPE.LOCAL_PAGE_STATE}/>

  return (
    <Default>
      <section style={{ 
        display: "flex", flexDirection: "column", gap: "1rem",
        marginInlineStart: 'var(--menu-width-collapsed)' }}>
        {/* Cabeçalho */}
        <div 
          id="oi-page-root" 
          style={{ display: 'grid', gridTemplateColumns: '1fr auto', placeItems: 'center', marginBlockStart: "2rem" }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'auto',
              inlineSize: "100%",
              alignItems: 'start', }}>
              <PageHeading>{t("productOpportunities")}</PageHeading>
              <p style={{ fontSize: '0.813rem', color: SYSTEM_COLORS.GRAY.DIM_GRAY, marginBlockStart: '-16px' }}>
                Ajustes na estratégia de produto no nível empresa que podem evitar ruptura e/ou estoque parado
              </p>
            </div>

            <Copilot ctx={{ userId: userId, tenantId: tenantId }} />
        </div>
            
        {/* Análise de oportunidades */}
        <section 
          style={{ 
            padding: 24, display: "flex", flexDirection: "column", gap: 18, borderRadius: "0.75rem",
            background: SYSTEM_COLORS.WHITE.CLOUD, border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`, 
            boxShadow: `0 1px 3px ${SYSTEM_COLORS.BLACK.BLACK_4_PERCENT}` }}>
          <hgroup style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 4, height: 28, borderRadius: 2, background: SYSTEM_COLORS.RED.CORAL_RED }} />
            <h5 style={{ fontSize: "1.25rem", fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
              Análise das Oportunidades
            </h5>
          </hgroup>

          <OpportunityCounter ctx={counterCtx} uiState={{ page: 'detailed_opportunities' }} />

          {/* Cards superiores - Oportunidades de Produto e trends */}
          <section style={{ display: "grid", gridTemplateColumns: "4fr 2fr", gap: "1rem", height: "600px" }}>
            <div style={{ minHeight: 0 }}>
              <ProductHierarchyCard
                actions={{ onPathChange: setHierarchyPath, onFocusProduct: handleFocusProduct }}              
                ctx={{ hierarchy: hierarchy, detailsByLevel: vm?.ctx?.opportunitiesDetails }}/>
            </div>

            <div style={{ minHeight: 0 }}>
              <TrendChangesPanel 
                actions={{ onFocusProduct: handleFocusProduct }} 
                ctx={{ 
                  hierarchy: hierarchy, 
                  trends: vm?.ctx?.opportunitiesTrends,
                  activeOppsWithTypes: vm?.ctx?.activeOpportunities?.activeOpportunities,
                }} />
            </div>
          </section>

        </section>

        <RecommendedActionsTable
          actions={{
            onProductKeysChange: setProductKeys,
            onClearFilters: handleClearFilters,
            handleRemoveOpportunity: vm?.actions?.handleRemoveOpportunity,
            resetModalState: vm?.actions?.resetModalState,
            setSelectedId: vm?.actions?.setSelectedId,
            setRemovalReason: vm?.actions?.setRemovalReason,
            resetOppError: vm?.actions?.resetOppError,
          }}        
          ctx={{
            productKeys,
            focusProduct,
            activeOppsWithTypes: vm?.ctx?.activeOpportunities?.activeOpportunities,
            options: vm?.ctx?.getOptions,
            removalReason: vm?.ctx?.removalReason,
            selectedId: vm?.ctx?.selectedId,
            products: vmAnalysis?.ctx?.products,
            opportunityTrendsDetails: vm?.ctx?.opportunitiesTrendsDetails,
            hierarchy: hierarchy,
            detailsByLevel: vm?.ctx?.opportunitiesDetails }}
           uiState={{ 
            isLoadingTrendsDetails: vm?.uiState?.isLoadingOpportunitiesTrendsDetails,
            updateOppError: vm?.uiState?.updateOppError
           }}/>
      </section>
    </Default>
  );
}